import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SecondaryAction } from '@ohgoodlord/design-system'
import { margins, paddings } from '@/common/spacing'
import { colors, greyscaleColorPalette } from '@/common/colors'
import { checkHasComplete } from '@/common/utils'
import { ProgressFlow, ProgressFlowAction, ProgressPayload } from '../types'
import { Pill } from './pill'
import {
  getActiveFlow,
  mapFlowIdToDisplayName,
  mapFlowStageToDisplayName,
} from './utils'

const ActionWrapper = styled.div`
  margin-top: 16px;
  padding: 0 12px;
`

const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledFlowContent = styled.div<{ $isExpanded: boolean; $contentHeight: number }>`
  display: flex;
  flex-direction: row;
  margin: ${margins.xxs} 0;
  align-items: center;
  gap: 22px;
  transition: 0.2s;
  height: ${({ $isExpanded, $contentHeight }) =>
    $isExpanded ? `${$contentHeight}px` : '25px'};
`

const StyledChevronWrapper = styled.button<{ $isExpanded: boolean }>`
  margin: 0 ${margins.xs};
  rotate: ${({ $isExpanded }) => ($isExpanded ? '180deg' : '0deg')};
  transition: 0.2s;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`

const Wrapper = styled.div<{ $isExpanded: boolean }>`
  transition: 0.2s;
  color: ${colors.botChatPrimary};
  opacity: ${({ $isExpanded }) => ($isExpanded ? '100' : '0')};
  width: 100%;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
  background-color: #ffffff;
  padding: ${paddings.m} 0;
`

const StyledFlowHeadingWrapper = styled.div<{ $isNextStage: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 700;
  width: 100%;
  text-align: left;
  font-size: 14px;
  gap: 15px;
  color: ${({ $isNextStage }) =>
    $isNextStage ? greyscaleColorPalette.greyscale4 : '#114f62'};
`

const StyledFlowNameWrapper = styled.div<{ $isNextStage: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  font-size: 12px;
  gap: 15px;
  color: ${({ $isNextStage }) =>
    $isNextStage ? greyscaleColorPalette.greyscale4 : '#114f62'};
`

const StyledFlowStatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 15px;
  justify-content: center;
`

const StyledFlowStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StyledFlowDivider = styled.div`
  border: 1px dashed #bdbdbd;
  transition: 0.2s;
  min-height: 23px;
  height: 100%;
  margin-left: 10px;
`

const StyledFlowStagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FlowNumber = styled.div<{ $isNextStage: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 30px;
  font-size: 12px;
  border: 2px solid
    ${({ $isNextStage }) =>
      $isNextStage ? greyscaleColorPalette.greyscale4 : '#114f62'};
  color: ${({ $isNextStage }) =>
    $isNextStage ? greyscaleColorPalette.greyscale4 : '#114f62'};
`

type FlowDataItem = ProgressFlow & { name: string }

export interface ProgressDetailsProps {
  progress: ProgressPayload
  isExpanded?: boolean
  onExit?: () => void
}

export const ProgressDetails = ({
  progress,
  isExpanded = false,
  onExit,
}: ProgressDetailsProps) => {
  const [expandedFlow, setExpandedFlow] = useState<string | undefined>(undefined)
  const hasInitializedExpandedFlow = useRef(false)

  const flowData = useMemo(
    (): FlowDataItem[] =>
      Object.keys(progress.flows).map(key => ({
        ...progress.flows[key],
        name: mapFlowIdToDisplayName(key),
      })),
    [progress.flows],
  )

  const isExitButtonVisible = JSON.parse(
    localStorage.getItem('hasSummaryPage') ?? 'false',
  )

  const activeFlow = useMemo(
    () => getActiveFlow(flowData, progress),
    [flowData, progress],
  )

  const activeFlowStage = useMemo(() => {
    if (!activeFlow) {
      return undefined
    }
    return activeFlow.actions.find(action => action.completed === false)
  }, [activeFlow])

  const hasCompletedApplication = checkHasComplete(progress)

  const expandFlowDetails = (flow: { id: string | null } | undefined) => {
    if (!flow?.id) {
      return
    }
    if (flow.id === expandedFlow) {
      setExpandedFlow('')
      return
    }
    setExpandedFlow(flow.id)
  }

  useEffect(() => {
    if (hasInitializedExpandedFlow.current) {
      return
    }
    if (checkHasComplete(progress) || !activeFlow?.id) {
      return
    }
    hasInitializedExpandedFlow.current = true
    setExpandedFlow(activeFlow.id)
  }, [progress, activeFlow])

  useEffect(() => {
    if (!isExpanded) {
      return
    }
    if (!checkHasComplete(progress)) {
      setExpandedFlow(undefined)
      const next = flowData.find(flow => flow.completed === false)
      if (next?.id) {
        setExpandedFlow(next.id)
      }
    }
  }, [isExpanded, progress, flowData])

  const isFlowExpanded = (flow: { id: string | null; completed: boolean }) =>
    expandedFlow === flow.id ||
    (expandedFlow === undefined && flow.id === activeFlow?.id && !flow.completed)

  return (
    <Wrapper $isExpanded={isExpanded}>
      {isExpanded && (
        <>
          {flowData.map((flow, index) => (
            <StyledFlowStatusWrapper key={flow.name}>
              <StyledFlowStatus>
                <StyledFlowHeadingWrapper
                  $isNextStage={!flow.completed && activeFlow?.id !== flow.id}
                >
                  <StyledIconWrapper>
                    {flow.completed ? (
                      <FontAwesomeIcon
                        icon="check-circle"
                        color="#6ABD40"
                        style={{ height: 22, width: 22 }}
                      />
                    ) : (
                      <FlowNumber
                        $isNextStage={!flow.completed && activeFlow?.id !== flow.id}
                      >
                        {index + 1}
                      </FlowNumber>
                    )}
                  </StyledIconWrapper>
                  {flow.name}
                </StyledFlowHeadingWrapper>
                <StyledChevronWrapper
                  type="button"
                  $isExpanded={isFlowExpanded(flow)}
                  onClick={() => expandFlowDetails(flow)}
                  aria-label={`Toggle ${flow.name}`}
                >
                  <FontAwesomeIcon icon="chevron-down" size="xl" color="#114F62" />
                </StyledChevronWrapper>
              </StyledFlowStatus>
              <StyledFlowContent
                $contentHeight={(flow.actions?.length || 0) * 45}
                $isExpanded={isFlowExpanded(flow)}
              >
                <StyledFlowDivider />
                {expandedFlow === flow.id && (
                  <StyledFlowStagesWrapper>
                    {flow.actions.map((action: ProgressFlowAction) => (
                      <div key={action.name || action.id}>
                        <StyledFlowStatus>
                          <StyledFlowNameWrapper
                            $isNextStage={!flow.completed && activeFlow?.id !== flow.id}
                          >
                            {mapFlowStageToDisplayName(action.id)}
                            {activeFlowStage && activeFlowStage.id === action.id && (
                              <Pill color="orange">In progress</Pill>
                            )}
                            {activeFlowStage &&
                              activeFlowStage.id !== action.id &&
                              !flow.completed &&
                              !action.completed && <Pill color="grey">Not started</Pill>}
                            {action.completed && <Pill color="green">Completed</Pill>}
                          </StyledFlowNameWrapper>
                        </StyledFlowStatus>
                      </div>
                    ))}
                  </StyledFlowStagesWrapper>
                )}
              </StyledFlowContent>
            </StyledFlowStatusWrapper>
          ))}
          <StyledFlowStatusWrapper>
            <StyledFlowStatus>
              <StyledFlowHeadingWrapper $isNextStage={!hasCompletedApplication}>
                <StyledIconWrapper>
                  {hasCompletedApplication ? (
                    <FontAwesomeIcon
                      icon="check-circle"
                      color="#6ABD40"
                      style={{ height: 22, width: 22 }}
                    />
                  ) : (
                    <FlowNumber $isNextStage={!hasCompletedApplication}>
                      {flowData.length + 1}
                    </FlowNumber>
                  )}
                </StyledIconWrapper>
                Done
              </StyledFlowHeadingWrapper>
            </StyledFlowStatus>
          </StyledFlowStatusWrapper>
        </>
      )}
      {isExitButtonVisible && (
        <ActionWrapper>
          <SecondaryAction onClick={() => onExit?.()} isFullWidth>
            Exit
          </SecondaryAction>
        </ActionWrapper>
      )}
    </Wrapper>
  )
}
